import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelectDropdown, MultiSelectDropdownOptions } from 'qampo-ui-library';
import { Toggle } from '../UI/base';
import type {
  StaffBookingRequest,
  StaffBookingResponse,
  StaffResponse
} from '../../api/generated';
import {
  BookingFormModalShell,
  BookingOccurrenceFields,
  useBookingFormState
} from '../Bookings/shared';
import { STAFF_BOOKING_TYPE } from './staffBookingConstants';

interface StaffBookingFormModalProps {
  booking: StaffBookingResponse | null;
  staff: StaffResponse[];
  onSave: (payload: StaffBookingRequest) => void;
  onClose: () => void;
}

const KEY_PREFIX = 'staff-booking';

export const StaffBookingFormModal = ({
  booking,
  staff,
  onSave,
  onClose
}: StaffBookingFormModalProps) => {
  const { t } = useTranslation();
  const form = useBookingFormState({ booking, keyPrefix: KEY_PREFIX });
  // staffIds is typed as a Set but arrives as a plain JSON array; Array.from handles both.
  const [appliesToAllStaff, setAppliesToAllStaff] = useState(booking?.appliesToAllStaff ?? false);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>(
    booking ? Array.from(booking.staffIds) : []
  );

  const selectedStaff = staff.filter((member) => selectedStaffIds.includes(member.id));

  const toggleStaff = (id: string) => {
    setSelectedStaffIds((ids) =>
      ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id]
    );
  };

  const handleSave = () => {
    const isValid = form.validate((next) => {
      if (!appliesToAllStaff && selectedStaffIds.length === 0) {
        next.staff = t('staff-booking-error-staff');
      }
    });
    if (!isValid) return;
    onSave({
      type: STAFF_BOOKING_TYPE,
      reason: form.reason.trim() || undefined,
      // appliesToAllStaff and staffIds are mutually exclusive on the backend: an all-staff booking
      // carries no ids (it always resolves to the current roster), a specific one carries the ids.
      // The Set type is a generated-client artefact; the body is JSON, so we send a plain array.
      appliesToAllStaff,
      staffIds: (appliesToAllStaff ? [] : selectedStaffIds) as unknown as Set<string>,
      time: form.buildTime()
    });
  };

  return (
    <BookingFormModalShell
      keyPrefix={KEY_PREFIX}
      isEditing={booking !== null}
      onSave={handleSave}
      onClose={onClose}
    >
      <Toggle
        label={t('staff-booking-all-staff-label')}
        checked={appliesToAllStaff}
        onChange={() => setAppliesToAllStaff((v) => !v)}
      />

      {!appliesToAllStaff && (
        <MultiSelectDropdown
          label={t('staff-booking-staff-label')}
          required
          placeholder={t('staff-booking-staff-placeholder')}
          selectedOptions={selectedStaff}
          displayOptionName={(member) => member.fullName}
          onRemoveOption={(member) => toggleStaff(member.id)}
          error={Boolean(form.errors.staff)}
          helperText={form.errors.staff}
        >
          <MultiSelectDropdownOptions
            options={staff}
            selectedOptions={selectedStaff}
            getKey={(member) => member.id}
            displayOption={(member) => member.fullName}
            handleSelectOption={(member) => toggleStaff(member.id)}
          />
        </MultiSelectDropdown>
      )}

      <BookingOccurrenceFields form={form} isCreating={booking === null} keyPrefix={KEY_PREFIX} />
    </BookingFormModalShell>
  );
};
